import React, { ReactNode, useState, useEffect, useMemo } from 'react'
import { Table, Panel, IconButton, Popover, Whisper, Checkbox } from 'rsuite'
import MoreIcon from '@rsuite/icons/More'
import type { RowDataType } from 'rsuite/esm/Table'
import './ResponsiveTable.scss'

export interface ResponsiveTableColumn<T = any> {
  dataKey: string
  header: string
  sortable?: boolean
  flexGrow?: number
  width?: number
  fixed?: 'left' | 'right' | boolean
  verticalAlign?: 'top' | 'middle' | 'bottom'
  hideOnMobile?: boolean
  hideByDefault?: boolean
  isCardTitle?: boolean
  isCardBadge?: boolean
  isCardImage?: boolean
  render?: (rowData: T, rowIndex?: number) => ReactNode
  renderMobile?: (rowData: T, rowIndex?: number) => ReactNode
}

export interface ResponsiveTableProps<T = any> {
  data: T[]
  columns: ResponsiveTableColumn<T>[]
  loading?: boolean
  rowKey?: string
  rowHeight?: number
  renderActions?: (rowData: T, rowIndex?: number) => ReactNode
  actionsHeader?: string
  sortColumn?: string
  sortType?: 'asc' | 'desc'
  onSortColumn?: (dataKey: string, sortType?: 'asc' | 'desc') => void
  emptyMessage?: string
  renderCard?: (rowData: T, rowIndex: number) => ReactNode
  storageKey?: string
  enableColumnToggle?: boolean
  renderToolbar?: () => ReactNode
  total?: number
  totalLabel?: string
}

const { Column, HeaderCell, Cell } = Table

export default function ResponsiveTable<T extends Record<string, any>> ({
  data,
  columns,
  loading,
  rowKey = 'id',
  rowHeight = 60,
  renderActions,
  actionsHeader = 'Управление',
  sortColumn,
  sortType,
  onSortColumn,
  emptyMessage = 'Нет данных',
  renderCard,
  storageKey,
  enableColumnToggle = true,
  renderToolbar,
  total,
  totalLabel = 'Найдено'
}: ResponsiveTableProps<T>) {
  // Получаем ключ для localStorage
  const localStorageKey = storageKey ? `table-columns-${storageKey}` : null

  // Инициализируем видимость колонок из localStorage или по умолчанию
  const getInitialVisibility = (): Record<string, boolean> => {
    // Сначала получаем дефолтные значения из конфигурации колонок
    const defaultVisibility = columns.reduce((acc, col) => {
      acc[col.dataKey] = !col.hideByDefault
      return acc
    }, {} as Record<string, boolean>)

    if (!localStorageKey) {
      return defaultVisibility
    }

    try {
      const saved = localStorage.getItem(localStorageKey)
      if (saved) {
        const savedVisibility = JSON.parse(saved)
        // Мержим сохраненные значения с дефолтными
        // Для новых колонок используем дефолтные значения
        return {
          ...defaultVisibility,
          ...savedVisibility
        }
      }
    }
    catch (e) {
      console.error('Error loading column visibility from localStorage:', e)
    }

    return defaultVisibility
  }

  const [ columnVisibility, setColumnVisibility ] = useState<Record<string, boolean>>(getInitialVisibility)

  // Сохраняем настройки в localStorage при изменении
  useEffect(() => {
    if (localStorageKey) {
      try {
        localStorage.setItem(localStorageKey, JSON.stringify(columnVisibility))
      }
      catch (e) {
        console.error('Error saving column visibility to localStorage:', e)
      }
    }
  }, [ columnVisibility, localStorageKey ])

  // Фильтруем видимые колонки
  const visibleColumns = useMemo(() => {
    return columns.filter(col => columnVisibility[col.dataKey] !== false)
  }, [ columns, columnVisibility ])

  // Обработчик изменения видимости колонки
  const handleColumnToggle = (dataKey: string) => {
    setColumnVisibility(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }))
  }

  // Получаем колонки для заголовка и бейджей карточки
  const titleColumn = visibleColumns.find(c => c.isCardTitle)
  const badgeColumns = visibleColumns.filter(c => c.isCardBadge)
  const imageColumns = visibleColumns.filter(c => c.isCardImage)
  const contentColumns = visibleColumns.filter(c => !c.isCardTitle && !c.isCardBadge && !c.isCardImage && !c.hideOnMobile)

  const renderCellContent = (column: ResponsiveTableColumn<T>, rowData: T, rowIndex?: number, isMobile = false) => {
    if (isMobile && column.renderMobile) {
      return column.renderMobile(rowData, rowIndex)
    }
    if (column.render) {
      return column.render(rowData, rowIndex)
    }
    return rowData[column.dataKey]
  }

  // Компонент управления видимостью колонок
  const renderColumnToggle = () => {
    if (!enableColumnToggle || !columns.length) return null

    const speaker = (
      <Popover title="Настройка колонок" style={{ width: 250 }}>
        <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {columns.map(col => (
            <Checkbox
              key={col.dataKey}
              checked={columnVisibility[col.dataKey] !== false}
              onChange={() => handleColumnToggle(col.dataKey)}
            >
              {col.header}
            </Checkbox>
          ))}
        </div>
      </Popover>
    )

    return (
      <Whisper placement="bottomEnd" trigger="click" speaker={speaker}>
        <IconButton icon={<MoreIcon />} size="sm" appearance="subtle" className="ml-auto">
          Колонки
        </IconButton>
      </Whisper>
    )
  }

  // Панель инструментов
  const renderToolbarSection = () => {
    if (!renderToolbar && !enableColumnToggle && total === undefined) return null

    return (
      <div className="ResponsiveTable__toolbar">
        {total !== undefined && (
          <div className="ResponsiveTable__total">
            {totalLabel}: <strong>{total}</strong>
          </div>
        )}
        {renderToolbar && renderToolbar()}
        {renderColumnToggle()}
      </div>
    )
  }

  // Мобильная версия - карточки
  const renderMobileCards = () => {
    if (loading) {
      return (
        <div className="ResponsiveTable__loading">
          <div className="animate-pulse space-y-4">
            {[ 1, 2, 3 ].map(i => (
              <div key={i} className="bg-gray-200 rounded-lg h-32" />
            ))}
          </div>
        </div>
      )
    }

    if (!data || data.length === 0) {
      return (
        <div className="ResponsiveTable__empty">
          {emptyMessage}
        </div>
      )
    }

    return (
      <div className="ResponsiveTable__cards">
        {data.map((rowData, index) => {
          if (renderCard) {
            return (
              <div key={rowData[rowKey] || index} className="ResponsiveTable__card">
                {renderCard(rowData, index)}
              </div>
            )
          }

          return (
            <Panel
              key={rowData[rowKey] || index}
              bordered
              className="ResponsiveTable__card mb-3"
            >
              {/* Заголовок карточки */}
              <div className="ResponsiveTable__card-header">
                <div className="ResponsiveTable__card-title">
                  {titleColumn ? renderCellContent(titleColumn, rowData, index, true) : `#${rowData[rowKey]}`}
                </div>
                {badgeColumns.length > 0 && (
                  <div className="ResponsiveTable__card-badges">
                    {badgeColumns.map(col => (
                      <span key={col.dataKey}>
                        {renderCellContent(col, rowData, index, true)}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Изображение на всю ширину */}
              {imageColumns.length > 0 && (
                <div className="ResponsiveTable__card-image">
                  {imageColumns.map(col => (
                    <div key={col.dataKey}>
                      {renderCellContent(col, rowData, index, true)}
                    </div>
                  ))}
                </div>
              )}

              {/* Контент карточки */}
              <div className="ResponsiveTable__card-content">
                {contentColumns.map(column => (
                  <div key={column.dataKey} className="ResponsiveTable__card-row">
                    <span className="ResponsiveTable__card-label">{column.header}:</span>
                    <span className="ResponsiveTable__card-value">
                      {renderCellContent(column, rowData, index, true)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Действия */}
              {renderActions && (
                <div className="ResponsiveTable__card-actions">
                  {renderActions(rowData, index)}
                </div>
              )}
            </Panel>
          )
        })}
      </div>
    )
  }

  // Десктопная версия - таблица rsuite
  const renderDesktopTable = () => (
    <Table
      autoHeight
      affixHeader
      loading={loading}
      data={data as RowDataType[]}
      rowHeight={rowHeight}
      sortColumn={sortColumn}
      sortType={sortType}
      onSortColumn={onSortColumn}
    >
      {visibleColumns.map(column => (
        <Column
          key={column.dataKey}
          sortable={column.sortable}
          flexGrow={column.flexGrow ?? 1}
          width={column.width}
          fixed={column.fixed}
          verticalAlign={column.verticalAlign ?? 'middle'}
        >
          <HeaderCell>{column.header}</HeaderCell>
          <Cell dataKey={column.dataKey}>
            {(rowData: T, rowIndex?: number) => renderCellContent(column, rowData, rowIndex)}
          </Cell>
        </Column>
      ))}

      {renderActions && (
        <Column flexGrow={2} fixed="right" verticalAlign="middle">
          <HeaderCell>{actionsHeader}</HeaderCell>
          <Cell>
            {(rowData: T, rowIndex?: number) => renderActions(rowData, rowIndex)}
          </Cell>
        </Column>
      )}
    </Table>
  )

  return (
    <div className="ResponsiveTable">
      {renderToolbarSection()}
      <div className="ResponsiveTable__mobile">
        {renderMobileCards()}
      </div>
      <div className="ResponsiveTable__desktop">
        {renderDesktopTable()}
      </div>
    </div>
  )
}
